import { isolines } from "@thi.ng/geom-isoline"
import { hash } from "object-code"
import { ALL_COLORS, type ColorScheme } from "../brand"
import { Bounds } from "./bounds"
import { Paths } from "./paths"
import type { Processor, Shape } from "./processor"
import { Mathf } from "./utils"
import { colord } from "colord"

type RendererArgs = {
  cellSize: number
  rows: number
  cols: number
  lineOffset: number
  shapes?: Shape[]
  scheme: ColorScheme
  padding: number
  processor: Processor
  onColorChange?(scheme: ColorScheme): void
}

export class MorphRenderer {
  args!: RendererArgs
  canvas!: HTMLCanvasElement
  ctx!: CanvasRenderingContext2D
  private raf = 0
  private needsResize = false

  public time = 0

  public renderScale = 2

  public rows: number = 0
  public cols: number = 0

  private scheme: ColorScheme = {
    bg: "black",
    bgColor: ALL_COLORS.black,
    fg: "white",
    fgColor: ALL_COLORS.white,
  }

  private shapes: Shape[] = []
  private queuedShapes: Shape[] | undefined
  private lastShapesSet = 0

  layers = new Map<number, AnimatedLayer>()

  setCanvas(canvas: HTMLCanvasElement) {
    this.canvas = canvas
    this.ctx = canvas.getContext("2d")!
  }

  setArgs(args: RendererArgs) {
    if (args.cellSize !== this.args?.cellSize) {
      for (const layer of this.layers.values()) {
        layer.state.cellSize = args.cellSize
        layer.needsPathUpdate = true
        layer.getResult()
      }
    }

    this.args = args
    this.needsResize = true
    this.lastShapesSet = 0

    if (args.shapes) {
      this.queueShapes(args.shapes)
    }
  }

  queueShapes(shapes: Shape[]) {
    this.queuedShapes = shapes
  }

  private setShapes(shapes: Shape[]) {
    this.args.shapes = shapes
    this.lastShapesSet = this.time

    // Group shapes by layerId
    const shapeGroups = new Map<number, Shape[]>()
    for (const shape of shapes) {
      if (!shapeGroups.has(shape.layerId)) {
        shapeGroups.set(shape.layerId, [shape])
      } else {
        shapeGroups.get(shape.layerId)!.push(shape)
      }
    }

    // Add/update layers
    for (const [layerId, shapes] of shapeGroups) {
      let layer: AnimatedLayer
      if (this.layers.has(layerId)) {
        layer = this.layers.get(layerId)!
      } else {
        layer = new AnimatedLayer(layerId)
        this.layers.set(layerId, layer)
      }
      layer.setTarget({
        cellSize: this.args.cellSize,
        cols: this.args.cols,
        rows: this.args.rows,
        lineOffset: this.args.lineOffset,
        shapes: shapes,
      })
    }

    // Hide unused layers
    for (const [layerId, layer] of this.layers) {
      if (!shapeGroups.has(layerId)) {
        layer.setTarget({
          cellSize: this.args.cellSize,
          cols: this.args.cols,
          rows: this.args.rows,
          lineOffset: this.args.lineOffset,
          shapes: [],
        })
      }
    }
  }

  start() {
    this.tick()
  }

  updateColors(dt = 0) {
    const target = this.args.scheme
    const current = this.scheme
    const speed = dt * 2
    let colorsChanged = false
    if (target.bgColor !== current.bgColor) {
      const bg = colord(current.bgColor).toRgb()
      const bg2 = colord(target.bgColor).toRgb()
      bg.r = Mathf.lerp(bg.r, bg2.r, speed)
      bg.g = Mathf.lerp(bg.g, bg2.g, speed)
      bg.b = Mathf.lerp(bg.b, bg2.b, speed)
      current.bgColor = colord(bg).toHex()
      colorsChanged = true
    }
    if (target.fgColor !== current.fgColor) {
      current.fgColor = target.fgColor
      colorsChanged = true
    }

    if (this.args.onColorChange) {
      this.args.onColorChange?.(current)
    }
  }

  private tick(t = 0) {
    if (this.needsResize) {
      this.resize()
      this.needsResize = false
    }

    // Calculate time delta
    const dt = t == 0 || this.time === 0 ? 0 : Math.min((t - this.time) / 1000, 30 / 1000)
    this.time = t

    const { ctx, args, canvas } = this
    const { processor } = this.args

    // this.updateColors(dt);

    // Update the processor drawing effects if needed
    if (processor.cullDraw(dt)) {
      processor.updateDraw()
    }
    if (processor.needsUpdate) {
      processor.finalize(false)
      processor.needsUpdate = false
      this.queueShapes(processor.shapes)
    }

    // If there are shapes queued up, load them into the buffers, but throttle it a bit
    if (this.queuedShapes) {
      if (this.time - this.lastShapesSet > 100) {
        this.setShapes(this.queuedShapes)
        this.queuedShapes = undefined
      }
    }

    // Update and draw each layer
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.save()

    if (!this.args.shapes?.length) return

    // Correct for stroke width
    const strokeWidth = (this.args.shapes?.[0].style.strokeWidth || 0) - this.args.lineOffset * this.args.cellSize * 2
    const scale = (canvas.width - strokeWidth * 2) / canvas.width
    ctx.translate(canvas.width / 2, canvas.height / 2)
    ctx.scale(scale, scale)
    ctx.translate(-canvas.width / 2, -canvas.height / 2)

    // Apply padding and scale
    ctx.scale(this.renderScale, this.renderScale)
    ctx.translate(args.padding, args.padding)

    for (const layer of this.layers.values()) {
      // Update the layer
      layer.update(dt)

      // Draw each shape in the layer
      const paths = layer.getResult()
      if (!layer.state.shapes.length) continue
      for (const [i, path] of paths.entries()) {
        const shape = layer.state.shapes[i] ?? layer.state.shapes[0]
        if (shape.style.fill !== "none") {
          ctx.fillStyle = shape.style.fill
          ctx.fill(path)
        }
        if (shape.style.stroke !== "none") {
          ctx.strokeStyle = shape.style.stroke
          ctx.lineWidth = shape.style.strokeWidth ?? 1
          ctx.stroke(path)
        }
        // ctx.fillStyle = "red"
        // ctx.fillText(
        //   shape.layerId.toString(),
        //   shape.bounds.centerX * this.args.cellSize,
        //   shape.bounds.centerY * this.args.cellSize,
        // )
      }
    }

    ctx.restore()

    // Schedule the next frame
    this.raf = requestAnimationFrame((t) => this.tick(t))
  }

  // getSVGString(width: number) {
  //   let svgString = ""
  //   const height = this.canvas.height / this.renderScale
  //   const cellSize = width / this.args.cols
  //   svgString += `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${this.canvas.width} ${this.canvas.height}">`
  //   for (const layer of this.layers.values()) {
  //     // Draw each shape in the layer
  //     const paths = layer.getResult()
  //     if (!layer.state.shapes.length) continue
  //     for (const [i, path] of paths.entries()) {
  //       const shape = layer.state.shapes[i] ?? layer.state.shapes[0]
  //       let fill = "none"
  //       let stroke = "black"
  //       let lineWidth = 1
  //       if (shape.style.fill !== "none") {
  //         fill = shape.style.fill
  //       }
  //       if (shape.style.stroke !== "none") {
  //         stroke = shape.style.stroke
  //         lineWidth = shape.style.strokeWidth ?? 1
  //       }
  //       const pathData = shape.getPath(cellSize, this.args.lineOffset)
  //       const pathString = Paths.stringify(pathData)
  //       svgString += `<path d="${pathString}" fill="${fill}" stroke="${stroke}" stroke-width="${lineWidth}"/>`
  //     }
  //   }

  //   svgString += `</svg>`

  //   return svgString
  // }

  stop() {
    cancelAnimationFrame(this.raf)
  }

  private resize() {
    const { cols, rows, cellSize, padding } = this.args

    this.canvas.width = cols * cellSize * this.renderScale + padding * 2 * this.renderScale
    this.canvas.height = rows * cellSize * this.renderScale + padding * 2 * this.renderScale
  }
}

type AnimatedPathProps = {
  cols: number
  rows: number
  shapes: Shape[]
  lineOffset: number
  cellSize: number
}

class AnimatedLayer {
  value!: Float32Array
  target!: Float32Array

  w = 0
  h = 0

  state!: AnimatedPathProps

  scale = 2
  hash = 0
  pad = 3

  needsUpdate = true
  needsPathUpdate = true

  lastValue: Path2D[] = []

  private bounds: Bounds = new Bounds()

  constructor(public id: number) {
    this.resize()
  }

  private resize() {
    this.value = this.createBuffer()
    this.target = this.createBuffer()
    this.needsPathUpdate = true
    this.needsUpdate = true
  }

  private createBuffer() {
    const buffer = new Float32Array((this.w + this.pad * 2) * (this.h + this.pad * 2) * this.scale * this.scale).fill(
      10,
    )
    // for (let y = 0; y <= this.h * this.scale; y++) {
    //   for (let x = 0; x <= this.w * this.scale; x++) {
    //     const index = this.indexAt(x, y);
    //     buffer[index] = 10;
    //   }
    // }
    return buffer
  }

  setTarget(state: AnimatedPathProps) {
    this.state = state

    if (state.rows !== this.h || state.cols !== this.w) {
      this.h = state.rows
      this.w = state.cols
      this.resize()
    }

    // Only update if something changed
    const nextHash = hash([
      state.shapes.map((s) => s.tracedPath),
      state.cellSize,
      state.lineOffset,
      state.cols,
      state.rows,
    ])
    if (this.hash === nextHash) {
      // Nothing to do! The input is the same
      return
    }
    this.hash = nextHash

    // Build a list of paths/polygons to rasterize
    const paths: {
      path: Paths.Command[]
      poly: [number, number][]
      bounds: Bounds
    }[] = []
    const bounds = new Bounds()
    for (const shape of state.shapes) {
      const path = shape.getPath(1, state.lineOffset)
      const poly = Paths.toPolygon(path, 8, 1)
      const shapeBounds = shape.bounds.clone()
      shapeBounds.expand(10)
      paths.push({ path, poly, bounds: shapeBounds })
      bounds.extendBounds(shapeBounds)
    }

    const updateBounds = this.bounds.clone().extendBounds(bounds)

    // Update the 'target' buffer by rasterizing the paths
    let changed = false
    for (let y = 0; y <= (this.h + this.pad * 2) * this.scale; y++) {
      for (let x = 0; x <= (this.w + this.pad * 2) * this.scale; x++) {
        if (!updateBounds.contains(x / this.scale, y / this.scale)) {
          this.target[this.indexAt(x, y)] = 10
          continue
        }
        const index = this.indexAt(x, y)
        let value = 10
        for (const item of paths) {
          if (!item.bounds.contains(x / this.scale, y / this.scale)) {
            continue
          }
          const dist = Paths.sdfPolygon([(x - this.pad) / this.scale, (y - this.pad) / this.scale], item.poly)
          const field = dist * 1
          value = Math.min(value, field)
        }
        this.target[index] = value
        changed = true
      }
    }

    this.bounds = bounds

    this.needsUpdate = changed
    this.needsPathUpdate = false
  }

  private indexAt(x: number, y: number) {
    return y * (this.w * this.scale + this.pad * 2 * this.scale) + x
  }

  update(dt = 0) {
    let changed = false
    if (this.needsUpdate) {
      for (let index = 0; index < this.value.length; index++) {
        if (this.value[index] === this.target[index]) continue
        const dist = Math.abs(this.value[index] - this.target[index])
        changed = true
        if (dist < 0.01) {
          this.value[index] = this.target[index]
        } else {
          this.value[index] = Mathf.lerp(this.value[index], this.target[index], dt * 8)
        }
      }
    }

    this.needsUpdate = changed
    this.needsPathUpdate = changed
  }

  getResult() {
    if (!this.needsPathUpdate) {
      return this.lastValue
    }

    const renderScale = this.state.cellSize / this.scale

    const outputShapes = [
      ...isolines(
        this.value,
        (this.w + this.pad * 2) * this.scale,
        (this.h + this.pad * 2) * this.scale,
        0,
        renderScale,
      ),
    ]

    let minDist = Infinity
    let maxDist = -Infinity
    const paths = outputShapes
      .map((rings) => {
        let lastX = rings.at(-1)![0]
        let lastY = rings.at(-1)![1]
        const points = rings.filter((p, i) => {
          const dist = Math.hypot(p[0] - lastX, p[1] - lastY)
          // if (i > 1) {
          //   if (dist < minDist) minDist = dist
          //   if (dist > maxDist) maxDist = dist
          // }
          lastX = p[0]
          lastY = p[1]
          const keep = dist > 0
          if (!keep) return false
          p[0] = p[0] - this.pad * renderScale - 0.5 * renderScale
          p[1] = p[1] - this.pad * renderScale - 0.5 * renderScale
          return true
        })
        if (rings.length < 6) return null
        // console.log(points)
        return Paths.catmullrom(points as [number, number][], 1)
      })
      .filter((p) => !!p)

    this.needsPathUpdate = false

    this.lastValue = paths

    return paths
  }
}
